import type { ViewContext } from '@shkola/api';
import { NewspaperIcon } from '@phosphor-icons/react/Newspaper';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  Block,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_SOFT,
  PageHead,
  PillIcon,
  TaskArt,
  grad,
  pillClass,
} from '../design/forms';
import {
  ArrowUpRightIcon,
  ChatCircleIcon,
  ClockIcon,
  type FilledIcon,
  HeartIcon,
  UsersThreeIcon,
} from '../design/icons';
import { HelpLines, IconDot, Private } from './shared';

/*
 * «Турбота» of a family in «Форми 2» (28.10): the title on the paper, the promise of privacy under it with
 * a lock and the way to the psychologist beside it; the five tips in one block (an icon in its colour, a
 * light title, the words); when not to wait on coral with the ringing clock big and quiet behind; the help
 * lines to call.
 */

const TIPS: ReadonlyArray<{
  key: 'listen' | 'name' | 'rhythm' | 'news' | 'help';
  icon: FilledIcon;
  tone: Grad;
}> = [
  { key: 'listen', icon: ChatCircleIcon, tone: 'lavender' },
  { key: 'name', icon: HeartIcon, tone: 'pink' },
  { key: 'rhythm', icon: ClockIcon, tone: 'butter' },
  { key: 'news', icon: NewspaperIcon, tone: 'sky' },
  { key: 'help', icon: UsersThreeIcon, tone: 'mint' },
];

/**
 * «Турбота» for families: how to support a child, the way to the psychologist, help lines —
 * and, said plainly, why the child's own conversations stay private.
 */
export function ParentCare({ ctx }: { ctx: Extract<ViewContext, { kind: 'guardian' }> }) {
  const { t } = useTranslation();
  const meet = (
    <Link to="/meetings" className={pillClass('dark')}>
      {t('care.parent.meet')}
      <PillIcon icon={ArrowUpRightIcon} />
    </Link>
  );
  return (
    <div className="flex flex-col gap-4">
      <PageHead
        title={t('care.parent.title')}
        note={ctx.child.firstName}
        sub={
          <Private className="max-w-[720px] !text-[15px] !leading-[22px]">
            {t('care.parent.privacy')}
          </Private>
        }
      >
        {/* beside the title from a tablet up; on a phone under it (the title keeps its width) */}
        <span className="hidden md:block">{meet}</span>
      </PageHead>
      <div className="-mt-1 md:hidden">{meet}</div>

      <Block>
        <ul className="grid grid-cols-1 gap-x-6 gap-y-6 sm:grid-cols-2 xl:grid-cols-5">
          {TIPS.map((tip) => (
            <li key={tip.key} className="flex min-w-0 gap-3.5 sm:flex-col sm:gap-3">
              <IconDot icon={tip.icon} tone={tip.tone} size={44} />
              <div className="flex min-w-0 flex-col gap-2 sm:gap-3">
                <h2
                  className="font-display text-[21px] leading-[26px] font-light tracking-[-0.02em]"
                  style={{ color: INK }}
                >
                  {t(`care.parent.tips.${tip.key}.title`)}
                </h2>
                <p className="text-[14px] leading-[21px]" style={{ color: INK_SOFT }}>
                  {t(`care.parent.tips.${tip.key}.body`)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </Block>

      <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <section
          className="relative isolate flex min-w-0 flex-col overflow-hidden rounded-[28px] p-5 md:p-6"
          style={{ backgroundImage: grad('coral'), color: GRAD.coral.ink }}
        >
          <TaskArt
            kind="overdue"
            tone="coral"
            size={170}
            ghost
            className="pointer-events-none absolute -right-6 -bottom-6 -z-10"
          />
          <h2
            className="font-display text-[30px] leading-[36px] font-light tracking-[-0.035em]"
            style={{ color: INK }}
          >
            {t('care.parent.signsTitle')}
          </h2>
          <p className="mt-3 max-w-[520px] text-[16px] leading-[25px]" style={{ color: INK }}>
            {t('care.parent.signs')}
          </p>
          <div className="mt-auto flex flex-wrap gap-2 pt-6">
            {(['7333', '112'] as const).map((phone) => (
              <a
                key={phone}
                href={`tel:${phone}`}
                className="inline-flex h-11 items-center rounded-full bg-white px-5 font-display text-[20px] font-light tracking-[-0.02em] transition-colors hover:bg-white/80"
                style={{ color: INK }}
              >
                {phone}
              </a>
            ))}
          </div>
        </section>
        <Block>
          <Head title={t('care.parent.lines')} />
          <HelpLines />
        </Block>
      </div>
    </div>
  );
}
